import * as React from 'react';
import { PieSvgProps, ResponsivePie } from '@nivo/pie';
import classNames from 'classnames';

import { ColorStyleProps } from '../../../@types/theme';
import { useTheme } from '../../../contexts/theme';
import Spinner from '../../atoms/Spinner';

import styles from './DonutChart.module.scss';

export interface ChartDatum {
  id: string;
  label: string;
  value: number;
  percentage?: number;
}

type ChartOptions = Partial<PieSvgProps<ChartDatum>>;
type TextColorProps = Exclude<ColorStyleProps, 'chart'>;
type DonutLayerProps = {
  centerX: number;
  centerY: number;
};

export interface Props extends React.HTMLAttributes<HTMLElement> {
  data: Array<ChartDatum>;
  options?: ChartOptions;
  height?: number;
  /** Rotation of the chart, in degrees. Accepts values from -360 to 360. */
  rotate?: number;
  value: number;
  subtitle?: string;
  valueColor?: TextColorProps;
  subtitleColor?: TextColorProps;
  fontSize?: number;
  loading?: boolean;
}

const defaultOptions: ChartOptions = {
  margin: {
    top: 16,
    left: 16,
    right: 16,
    bottom: 16,
  },
  innerRadius: 0.72,
  enableArcLabels: false,
  enableArcLinkLabels: false,
  arcLabelsSkipAngle: 360,
  legends: [],
  isInteractive: false,
};

const parseFontSizeToPx = (fontSize: string): number => {
  const parsed = Number.parseFloat(fontSize);
  if (Number.isNaN(parsed)) return 0;
  if (fontSize.endsWith('rem')) return parsed * 16;
  return parsed;
};

interface CreateCenteredMetricLayerArg {
  themeFontFamily: string;
  valueFontSize: string;
  valueFontWeight: string;
  subtitleFontSize: string;
  subtitleFontWeight: string;
  value: number;
  subtitle?: string;
  valueColor?: string;
  subtitleColor?: string;
  fontSize?: number;
}

const createCenteredMetricLayer =
  ({
    themeFontFamily,
    valueFontSize,
    valueFontWeight,
    subtitleFontSize,
    subtitleFontWeight,
    value,
    subtitle,
    valueColor,
    subtitleColor,
    fontSize,
  }: CreateCenteredMetricLayerArg) =>
  ({ centerX, centerY }: DonutLayerProps) => {
    const subtitleExists = !!subtitle;
    const scale = fontSize ?? 1;
    const safeScale = Number.isFinite(scale) && scale > 0 ? scale : 1;
    const valueSizePx = parseFontSizeToPx(valueFontSize);
    const subtitleSizePx = parseFontSizeToPx(subtitleFontSize);
    const linesGapPx = Math.max(4, valueSizePx * 0.12);
    const valueY = subtitleExists ? -(subtitleSizePx / 2 + linesGapPx / 2) : 0;
    const subtitleY = valueSizePx / 2 + linesGapPx / 2;

    return (
      <g transform={`translate(${centerX}, ${centerY}) scale(${safeScale})`}>
        <text
          x={0}
          y={valueY}
          textAnchor="middle"
          dominantBaseline="central"
          style={{
            fill: valueColor,
            fontFamily: themeFontFamily,
            fontSize: valueFontSize,
            fontWeight: valueFontWeight,
          }}
        >
          {value}
        </text>
        {subtitleExists && (
          <text
            x={0}
            y={subtitleY}
            textAnchor="middle"
            dominantBaseline="central"
            style={{
              fill: subtitleColor,
              fontFamily: themeFontFamily,
              fontSize: subtitleFontSize,
              fontWeight: subtitleFontWeight,
            }}
          >
            {subtitle}
          </text>
        )}
      </g>
    );
  };

/**
 * <DonutChart> component
 */
export const DonutChart: React.FunctionComponent<Props> = ({
  className,
  data,
  loading,
  options = {},
  height = 220,
  rotate = -90,
  value,
  subtitle,
  valueColor = 'blackLight',
  subtitleColor = 'gray300',
  fontSize = 1,
  ...htmlElementProps
}: Props) => {
  const { theme } = useTheme();
  const fontFamily = React.useMemo(
    () =>
      Array.isArray(theme.typography.fontFamily)
        ? theme.typography.fontFamily.join(', ')
        : theme.typography.fontFamily,
    [theme.typography.fontFamily],
  );

  const centeredMetricLayer = React.useMemo(
    () =>
      createCenteredMetricLayer({
        themeFontFamily: fontFamily,
        valueFontSize: theme.typography.fontSize.desktop.text.lg3,
        valueFontWeight: theme.typography.fontWeight.bold,
        subtitleFontSize: theme.typography.fontSize.desktop.subtitle.sm2,
        subtitleFontWeight: theme.typography.fontWeight.regular,
        value,
        subtitle,
        valueColor: theme.color[valueColor],
        subtitleColor: theme.color[subtitleColor],
        fontSize,
      }),
    [
      fontFamily,
      value,
      subtitle,
      theme.color,
      theme.typography.fontSize.desktop.text.lg3,
      theme.typography.fontSize.desktop.subtitle.sm2,
      theme.typography.fontWeight.bold,
      theme.typography.fontWeight.regular,
      valueColor,
      subtitleColor,
      fontSize,
    ],
  );

  const opts = React.useMemo(
    () => ({
      ...defaultOptions,
      ...options,
      startAngle:
        (options.startAngle ?? defaultOptions.startAngle ?? 0) + rotate,
      endAngle: (options.endAngle ?? defaultOptions.endAngle ?? 360) + rotate,
      layers: ['arcs', centeredMetricLayer],
      colors: options.colors || [theme.color.primary, theme.color.grayLight2],
      theme: {
        text: {
          fontFamily,
        },
      },
    }),
    [options, centeredMetricLayer, fontFamily, theme.color, rotate],
  );

  return (
    <div
      className={classNames(styles.wrapper, className)}
      {...htmlElementProps}
    >
      {loading && <Spinner data-testid="donutchart-spinner" />}

      {!loading && (
        <div style={{ height, width: height }} data-testid="chart-wrapper">
          <ResponsivePie
            data={data}
            {...(opts as Omit<PieSvgProps<ChartDatum>, 'data'>)}
          />
        </div>
      )}
    </div>
  );
};

export default DonutChart;
